import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  makeStyles,
  MuiThemeProvider,
  createMuiTheme,
  Modal,
  Box,
  Chip,
  Typography,
} from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import {
  deleteReloj,
  getRelojById,
  postReloj,
  updateReloj,
} from "../../../../api/reloj";
import { getMarcas } from "../../../../api/marca";
import { getModelos, postModelo } from "../../../../api/modelo";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { Autocomplete } from "@material-ui/lab";
import { TarjetaModelo } from "../../../components/TarjetaModelo";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { SERVER_URL } from "../../../../api";
import { uploadSingleFile } from "../../../../api/files";
import { AddOutlined, PlusOne, ArrowUpward, ArrowDownward } from "@material-ui/icons";
import EditModeloPage from "../modelo/EditModeloPage";
import Editor from "../../../components/editor/Editor";

// Función para construir URL de imagen correctamente (soporta imágenes de WordPress y blob)
const buildImageUrl = (imageUrl, serverUrl) => {
  if (!imageUrl) return null;

  // blob: solo existe en el navegador; no anteponer SERVER_URL (evita GET /blob:... al API)
  if (imageUrl.startsWith('blob:')) {
    return imageUrl;
  }

  // Si ya es una URL completa (http/https), devolverla tal cual
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // Normalizar serverUrl (eliminar trailing slash si existe)
  const normalizedServerUrl = serverUrl?.endsWith('/') ? serverUrl.slice(0, -1) : serverUrl;

  // Si empieza con /uploads, concatenar directamente con SERVER_URL
  if (imageUrl.startsWith('/uploads')) {
    return `${normalizedServerUrl}${imageUrl}`;
  }

  // Si empieza con uploads/ (sin / inicial), agregar / antes
  if (imageUrl.startsWith('uploads/')) {
    return `${normalizedServerUrl}/${imageUrl}`;
  }

  // Para cualquier otro caso, asumir que es relativo y agregar /
  return `${normalizedServerUrl}/${imageUrl}`;
};

const theme = createMuiTheme({
  palette: {
    secondary: {
      main: "#F64E60",
    },
  },
});

function SectionTitle({ children, first }) {
  return (
    <Typography
      variant="subtitle1"
      style={{
        fontWeight: 600,
        marginTop: first ? 0 : 24,
        marginBottom: 12,
        color: "#374151",
        borderBottom: "1px solid #e5e7eb",
        paddingBottom: 6,
      }}
    >
      {children}
    </Typography>
  );
}

const useStyles = makeStyles(() => ({
  dialogContent: {
    padding: 0,
    overflow: "hidden",
  },
  fancyLoader: {
    color: "#AF9A51 ",
    animation: `$spin 1s linear infinite`,
  },
  "@keyframes spin": {
    "0%": {
      transform: "rotate(0deg)",
    },
    "100%": {
      transform: "rotate(360deg)",
    },
  },
}));

export default function EditRelojPage() {
  const [reloj, setReloj] = useState({
    active: true,
    sold: false,
    stock: true,
    reservado: false,
    destacado: false,
    novedad: false,
    sku: "",
    name: "",
    brand: "",
    price: {
      amount: "",
      currency: "EUR",
    },
    description: "",
    seller: {
      name: "",
      country: "",
    },
    specifications: {
      marca: "",
      reloj_modelo: "",
      n_mero_de_referencia: "",
      a_o_de_fabricaci_n: "",
      material_de_la_caja: "",
      esfera: "",
      di_metro: "",
      estado: {
        usado: false,
        calificacion: "",
        descripcion: "",
      },
      contenido_de_la_entrega: {
        estuche: false,
        documento: false,
      },
    },
    images: [],
    pendingApproval: false,
  });
  const [marcas, setMarcas] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [selectedMarca, setSelectedMarca] = useState(null);
  const [selectedModelo, setSelectedModelo] = useState(null);
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const relojId = useParams().id;
  const classes = useStyles();
  const [openAltaModelo, setOpenAltaModelo] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [modeloTouched, setModeloTouched] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const limit = 100;
  const [selectedImages, setSelectedImages] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [newPreviews, setNewPreviews] = useState([]);
  const fileInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const modelosRef = useRef([]);
  const [suggestionsMaterialCaja, setSuggestionsMaterialCaja] = useState([]);
  const [suggestionsEsfera, setSuggestionsEsfera] = useState([]);
  const [suggestionsDiametro, setSuggestionsDiametro] = useState([]);

  const normalizeRef = (v) => String(v ?? "").trim().toLowerCase();
  const currentRef = (reloj?.specifications?.n_mero_de_referencia ?? reloj?.sku ?? "").toString().trim();
  const currentRefLower = normalizeRef(currentRef);

  // Año de fabricación: misma lógica que en la página de producto (specifications, data.specifications, o regex en name/description)
  const getYearFromReloj = (r) => {
    const specs = r?.specifications || {};
    const dataSpecs = r?.data?.specifications || {};
    const yearKeys = ["a_o_de_fabricaci_n", "año_de_fabricacion"];
    for (const key of yearKeys) {
      const val = specs[key] ?? dataSpecs[key];
      if (val != null && String(val).trim() !== "") return String(val).trim();
    }
    const text = [r?.name, r?.description].filter(Boolean).join(" ");
    const match = text.match(/\b(19\d{2}|20\d{2})\b/);
    return match ? match[1] : "";
  };
  const yearFromSpecs = getYearFromReloj(reloj);
  const hasExactModeloMatchForRef =
    !!currentRefLower &&
    (modelosRef.current || []).some((m) => normalizeRef(m?.sku) === currentRefLower);

  const createModeloOption =
    selectedMarca && currentRef
      ? {
          _id: "__create_model__",
          __create: true,
          nombre: `Crear modelo para ref. ${currentRef}`,
          sku: currentRef,
          marca: selectedMarca?._id,
        }
      : {
          _id: "__create_model__",
          __create: true,
          nombre: "Crear modelo",
        };

  const modelosSelectOptions =
    selectedMarca && currentRefLower && !hasExactModeloMatchForRef
      ? [createModeloOption, ...(modelos || [])]
      : modelos || [];

  const pickBestModeloForCurrentRef = () => {
    if (!currentRefLower) return null;
    const list = modelosRef.current || modelos || [];
    const exact = list.filter((m) => normalizeRef(m?.sku) === currentRefLower);
    return exact.length ? exact[0] : null;
  };

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const resMarcas = await getMarcas();

        if (resMarcas.status === 200) {
          setMarcas(resMarcas.data);
        } else {
          setLoading(false);
          alertError({ customMessage: "Could not get marcas." });
        }
        const resModelos = await getModelos({
          page: 1,
          limit,
          searchTerm,
        });
        if (resModelos.status === 200) {
          setPage(1);
          setModelos(resModelos.data.data);
          modelosRef.current = resModelos.data.data;
          setHasMore(true);
        } else {
          setLoading(false);
          alertError({ customMessage: "Could not get modelos." });
        }
        if (relojId) {
          getRelojById(relojId)
            .then((resReloj) => {
              if (resReloj.status === 200) {
                const dataSpecs = resReloj.data?.data?.specifications || {};
                const topSpecs = resReloj.data?.specifications || {};
                const mergedSpecs = { ...dataSpecs, ...topSpecs };
                setReloj({
                  ...resReloj.data,
                  views: resReloj.data?.views ?? 0,
                  specifications: mergedSpecs,
                });
                const marca = resMarcas.data.find(
                  (x) => x._id === resReloj.data.brand
                );
                const modelo = resReloj.data.specifications.reloj_modelo;
                setSelectedMarca(marca);
                setSelectedModelo(modelo);
                setModelos((prevModelos) => {
                  const nuevos = [...prevModelos, modelo];
                  modelosRef.current = nuevos;
                  return nuevos;
                });
              }
            })
            .catch((error) => {
              setLoading(false);
              alertError({
                error,
                customMessage: "Could not get reloj details.",
              });
              history.push("/relojes");
            });
        }
      } catch (error) {
        setLoading(false);
        alertError({ error: error });
      }
    };

    fetchData();
    setLoading(false);
  }, [relojId, history]);

  // Auto-buscar modelos sugeridos cuando hay número de referencia (ref) y marca
  useEffect(() => {
        // Si no hay marca seleccionada, limpiar modelos
        if (!selectedMarca || !selectedMarca._id) {
          setModelos([]);
          modelosRef.current = [];
          return;
        }

    // Si está cargando, no hacer nada
    if (loading) {
      return;
    }

    const buscarModelosSugeridos = async () => {
      const refTrimmed = currentRef;
      
      // Si hay ref. (número de referencia), buscar modelos con esa ref. y marca
      // Si no hay ref., cargar todos los modelos de la marca
      const searchValue = refTrimmed.length > 0 ? refTrimmed : "";
      
      console.log("[SUGERENCIAS] Buscando modelos con:", {
        marca: selectedMarca.nombre,
        ref: refTrimmed,
        searchValue
      });

      try {
        const params = {
          page: 1,
          limit: 100,
          searchTerm: searchValue,
          marca: selectedMarca._id,
        };
        
        console.log("[SUGERENCIAS] Params enviados:", params);
        const response = await getModelos(params);
        console.log("[SUGERENCIAS] Response recibida:", response);
        console.log("[SUGERENCIAS] Response status:", response.status);
        console.log("[SUGERENCIAS] Response.data:", response.data);
        
        // Manejar diferentes estructuras de respuesta
        let modelosData = [];
        if (response && response.data) {
          if (Array.isArray(response.data)) {
            modelosData = response.data;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            modelosData = response.data.data;
          } else if (response.data.modelos && Array.isArray(response.data.modelos)) {
            modelosData = response.data.modelos;
          }
        } else if (Array.isArray(response)) {
          modelosData = response;
        }
        
        console.log("[SUGERENCIAS] Modelos extraídos:", modelosData.length);
        if (modelosData.length > 0) {
          console.log("[SUGERENCIAS] Primeros modelos:", modelosData.slice(0, 3));
        }
        
        // Ordenar modelos por coincidencias con las especificaciones del reloj (ref = número de referencia)
        const modelosOrdenados = ordenarModelosPorCoincidencias(
          modelosData,
          reloj.specifications,
          currentRef
        );
        
        console.log("[SUGERENCIAS] Modelos ordenados por coincidencias:", modelosOrdenados.length);
        
        // Actualizar la lista de modelos sugeridos
        setModelos(modelosOrdenados);
        modelosRef.current = modelosOrdenados;
        
      } catch (error) {
        console.error("[SUGERENCIAS] Error buscando modelos sugeridos:", error);
        console.error("[SUGERENCIAS] Error details:", error.response?.data || error.message);
        setModelos([]);
        modelosRef.current = [];
      }
    };

    // Debounce para evitar búsquedas excesivas
    const timeoutId = setTimeout(buscarModelosSugeridos, 300);
    return () => clearTimeout(timeoutId);
  }, [currentRef, selectedMarca, loading, reloj.specifications?.material_de_la_caja, reloj.specifications?.esfera, reloj.specifications?.di_metro]);

  // Resetear modeloTouched cuando cambian los campos relevantes (para permitir auto-selección)
  useEffect(() => {
    // Si cambian marca, SKU, material, esfera o diámetro, permitir auto-selección de nuevo
    setModeloTouched((prev) => (prev ? false : prev));
  }, [selectedMarca?._id, currentRef, reloj.specifications?.material_de_la_caja, reloj.specifications?.esfera, reloj.specifications?.di_metro]);

  // Autocompletar modelo: seleccionar el primero de la lista cuando cambian los campos relevantes
  useEffect(() => {
    // Si el usuario ha tocado el campo manualmente, no auto-seleccionar
    if (modeloTouched) return;
    
    // Si no hay marca, no hacer nada
    if (!selectedMarca?._id) return;
    
    // Si hay ref. pero NO hay coincidencia exacta por ref., dejar modelo vacío
    // (y la primera sugerencia será "Crear modelo")
    if (currentRefLower && !hasExactModeloMatchForRef) {
      if (selectedModelo) {
        setSelectedModelo(null);
      }
      if (searchTerm) {
        setSearchTerm("");
      }
      return;
    }

    // Si no hay modelos cargados, no hacer nada
    const modelosList = modelosRef.current || modelos || [];
    if (modelosList.length === 0) return;

    // Seleccionar el primer modelo de la lista (ya está ordenado por coincidencias)
    const primerModelo = modelosList[0];
    
    // Solo actualizar si el modelo seleccionado es diferente. Solo auto-rellenar modelo, no SKU ni specs.
    if (selectedModelo?._id !== primerModelo?._id) {
      setSelectedModelo(primerModelo);
      setSearchTerm(primerModelo?.nombre || "");
    }
  }, [modelos, selectedMarca, currentRef, reloj.specifications?.material_de_la_caja, reloj.specifications?.esfera, reloj.specifications?.di_metro, selectedModelo, modeloTouched]);

  // Obtener sugerencias de material de caja, esfera y diámetro
  // Basadas en MODELOS con la misma marca y el mismo número de referencia
  useEffect(() => {
    const obtenerSugerencias = async () => {
      // Solo obtener sugerencias si hay marca y número de referencia
      const refBuscado = currentRef;
      if (!selectedMarca?._id || !refBuscado) {
        setSuggestionsMaterialCaja([]);
        setSuggestionsEsfera([]);
        setSuggestionsDiametro([]);
        return;
      }

      try {
        // Pedimos modelos sugeridos (ya sabemos que existen por tu log del /api/modelo)
        const params = {
          page: 1,
          limit: 200,
          searchTerm: refBuscado,
          marca: selectedMarca._id,
        };
        const response = await getModelos(params);

        if (response.status === 200) {
          // Manejar diferentes estructuras de respuesta
          const modelosData = Array.isArray(response.data)
            ? response.data
            : Array.isArray(response.data?.data)
            ? response.data.data
            : Array.isArray(response.data?.modelos)
            ? response.data.modelos
            : [];

          const refLower = refBuscado.toLowerCase();

          // Extraer valores únicos de material/esfera/diámetro SOLO de modelos con ref. exacta
          const materialesCaja = new Set();
          const esferas = new Set();
          const diametros = new Set();

          modelosData.forEach((m) => {
            const skuModelo = (m?.sku ?? "").toString().trim().toLowerCase();
            if (skuModelo !== refLower) return;

            const material = (m?.materialCaja ?? "").toString().trim();
            const esfera = (m?.colorEsfera ?? "").toString().trim();
            const diametro = (m?.diametro ?? "").toString().trim();

            if (material) materialesCaja.add(material);
            if (esfera) esferas.add(esfera);
            if (diametro) diametros.add(diametro);
          });

          setSuggestionsMaterialCaja(Array.from(materialesCaja).sort());
          setSuggestionsEsfera(Array.from(esferas).sort());
          setSuggestionsDiametro(Array.from(diametros).sort());
        }
      } catch (error) {
        console.error("Error obteniendo sugerencias:", error);
        setSuggestionsMaterialCaja([]);
        setSuggestionsEsfera([]);
        setSuggestionsDiametro([]);
      }
    };

    const timeoutId = setTimeout(obtenerSugerencias, 300);
    return () => clearTimeout(timeoutId);
  }, [selectedMarca, currentRef]);

  // Reordenar modelos cuando cambien las especificaciones
  useEffect(() => {
    if (modelosRef.current.length > 0 && (reloj.specifications?.material_de_la_caja || reloj.specifications?.esfera || reloj.specifications?.di_metro)) {
      const modelosOrdenados = ordenarModelosPorCoincidencias(
        [...modelosRef.current],
        reloj.specifications,
        currentRef
      );
      // Solo actualizar si el orden cambió (comparar IDs en las primeras posiciones)
      const ordenCambio = modelosOrdenados.length > 0 && modelosRef.current.length > 0 && 
        (modelosOrdenados[0]?._id !== modelosRef.current[0]?._id || 
         modelosOrdenados.slice(0, 3).map(m => m._id).join(',') !== modelosRef.current.slice(0, 3).map(m => m._id).join(','));
      if (ordenCambio) {
        setModelos(modelosOrdenados);
        modelosRef.current = modelosOrdenados;
      }
    }
  }, [reloj.specifications?.material_de_la_caja, reloj.specifications?.esfera, reloj.specifications?.di_metro, currentRef]);

  const handleChange = (field) => (event) => {
    setReloj({ ...reloj, [field]: event.target.value });
  };

  const handlePriceChange = (field) => (event) => {
    setReloj({
      ...reloj,
      price: { ...reloj.price, [field]: event.target.value },
    });
  };

  const handleSpecChange = (field) => (event) => {
    let value = "";
    
    // Si es un evento de input (tiene target.value)
    if (event?.target?.value !== undefined) {
      value = event.target.value;
    }
    // Si es un string directamente
    else if (typeof event === "string") {
      value = event;
    }
    // Si es un número
    else if (typeof event === "number") {
      value = String(event);
    }
    // Si es null o undefined
    else if (event == null) {
      value = "";
    }
    // Si es un objeto (ej. opción de Autocomplete), intentar extraer el valor
    else if (typeof event === "object") {
      // Intentar obtener propiedades comunes de opciones
      value = event.value ?? event.label ?? event.nombre ?? event.name ?? "";
    }
    
    setReloj({
      ...reloj,
      specifications: { ...reloj.specifications, [field]: value },
    });
  };

  const handleImageFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setSelectedImages((prev) => [...prev, ...files]);
    const previews = files.map((f) => URL.createObjectURL(f));
    setNewPreviews((prev) => [...prev, ...previews]);
  };

  const openImagePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const removeImage = (index) => {
    const existingCount = (reloj.images || []).length;
    if (index < existingCount) {
      const updatedExisting = [...(reloj.images || [])];
      updatedExisting.splice(index, 1);
      setReloj({ ...reloj, images: updatedExisting });
    } else {
      const newIndex = index - existingCount;
      const updatedSelected = selectedImages.filter((_, i) => i !== newIndex);
      const updatedPreviews = newPreviews.filter((_, i) => i !== newIndex);
      const toRevoke = newPreviews[newIndex];
      if (toRevoke) URL.revokeObjectURL(toRevoke);
      setSelectedImages(updatedSelected);
      setNewPreviews(updatedPreviews);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const existingCount = (reloj.images || []).length;
    const allImages = [...(reloj.images || []), ...newPreviews];
    
    // Reordenar
    const reorderedImages = [...allImages];
    const [draggedItem] = reorderedImages.splice(draggedIndex, 1);
    reorderedImages.splice(dropIndex, 0, draggedItem);

    // Separar imágenes existentes de las nuevas
    const updatedExisting = reorderedImages.slice(0, existingCount);
    const updatedPreviews = reorderedImages.slice(existingCount);

    setReloj({ ...reloj, images: updatedExisting });
    setNewPreviews(updatedPreviews);
    setDraggedIndex(null);
  };

  const moveImage = (index, direction) => {
    const existingCount = (reloj.images || []).length;
    const allImages = [...(reloj.images || []), ...newPreviews];
    
    if (direction === "up" && index === 0) return;
    if (direction === "down" && index === allImages.length - 1) return;

    const newIndex = direction === "up" ? index - 1 : index + 1;
    const reorderedImages = [...allImages];
    [reorderedImages[index], reorderedImages[newIndex]] = [reorderedImages[newIndex], reorderedImages[index]];

    const updatedExisting = reorderedImages.slice(0, existingCount);
    const updatedPreviews = reorderedImages.slice(existingCount);

    setReloj({ ...reloj, images: updatedExisting });
    setNewPreviews(updatedPreviews);
  };

  const uploadImages = async (files) => {
    const uploadedUrls = [];
    for (const file of files) {
      try {
        const response = await uploadSingleFile(file, "reloj");
        const url = response.data?.fileURL || response.data?.url || response.data?.path;
        if (url) {
          uploadedUrls.push(url);
        } else {
          throw new Error("No URL returned from upload");
        }
      } catch (uploadError) {
        const reader = new FileReader();
        const base64 = await new Promise((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        uploadedUrls.push(base64);
      }
    }
    return uploadedUrls;
  };

  const handleEstadoChange = (key) => (event) => {
    const value = key === "usado" ? event.target.checked : event.target.value;
    setReloj({
      ...reloj,
      specifications: {
        ...reloj.specifications,
        estado: {
          ...(reloj.specifications?.estado || {}),
          [key]: value,
        },
      },
    });
  };

  const handleContenidoChange = (key) => (event) => {
    setReloj({
      ...reloj,
      specifications: {
        ...reloj.specifications,
        contenido_de_la_entrega: {
          ...(reloj.specifications?.contenido_de_la_entrega || {}),
          [key]: event.target.checked,
        },
      },
    });
  };

  const isBase64Image = (value) =>
    typeof value === "string" && /^data:image\//i.test(value.trim());

  const handleImagesChange = (event) => {
    const lines = event.target.value
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      // Evitar que se guarden data URIs desde este textarea
      // (y también evita que al pegar base64 se haga enorme)
      .filter((l) => !isBase64Image(l));

    // Mantener las imágenes base64 que ya existan en el reloj, pero no mostrarlas aquí
    const existingBase64 = (reloj.images || []).filter(isBase64Image);
    setReloj({
      ...reloj,
      images: [...existingBase64, ...lines],
    });
  };

  const handleSave = async () => {
    if (!selectedMarca) {
      alertError({
        customMessage: "Es necesario rellenar la marca",
      });
      return;
    }

    // Si no hay modelo seleccionado, crear uno automáticamente (por ref. + marca)
    let modeloFinal = selectedModelo;
    if (!modeloFinal && selectedMarca && currentRef) {
      try {
        const nuevoModelo = {
          nombre: reloj.name || `Modelo ${currentRef}`,
          sku: currentRef,
          marca: selectedMarca._id,
          materialCaja: reloj.specifications?.material_de_la_caja || "",
          colorEsfera: reloj.specifications?.esfera || "",
          diametro: reloj.specifications?.di_metro || "",
          active: true,
        };
        const createdModelo = await postModelo(nuevoModelo);
        if (createdModelo.status === 200 || createdModelo.status === 201) {
          modeloFinal = createdModelo.data;
          setSelectedModelo(modeloFinal);
          alertSuccess({
            customMessage: `Modelo "${modeloFinal.nombre}" creado automáticamente.`,
          });
        }
      } catch (error) {
        alertError({
          error,
          customMessage: "No se pudo crear el modelo automáticamente. Por favor, selecciona un modelo.",
        });
        return;
      }
    }

    if (!modeloFinal) {
      alertError({
        customMessage: "Es necesario rellenar la marca y el número de referencia (o código interno) para crear o vincular el modelo",
      });
      return;
    }

    reloj.brand = selectedMarca._id;
    reloj.specifications.marca = selectedMarca._id;
    reloj.specifications.reloj_modelo = modeloFinal._id;
    reloj.propio = true;

    try {
      let finalImages = reloj.images || [];
      if (selectedImages.length > 0) {
        const uploaded = await uploadImages(selectedImages);
        finalImages = [...finalImages, ...uploaded];
      }
      const priceAmount = reloj.price?.amount != null && String(reloj.price.amount).trim() !== ""
        ? String(reloj.price.amount).trim()
        : "0";
      const payload = {
        ...reloj,
        images: finalImages,
        price: { ...reloj.price, amount: priceAmount, currency: reloj.price?.currency || "EUR" },
      };
      if (!relojId) {
        await postReloj(payload);
      } else {
        await updateReloj(relojId, payload);
      }
      alertSuccess({
        title: "Saved!",
        customMessage: "Reloj saved successfully.",
      });
      history.push("/relojes");
    } catch (error) {
      alertError({ error, customMessage: "Could not save reloj details." });
    }
  };

  const handleClose = (modelo) => {
    if (modelo) {
      setModelos((prevModelos) => {
        const nuevos = [...prevModelos, modelo];
        modelosRef.current = nuevos;
        return nuevos;
      });
      setSelectedModelo(modelo);
    }
    setOpenAltaModelo(false);
  };

  const cargarModelos = async (valor) => {
    try {
      const params = {
        page: 1,
        limit,
        searchTerm: valor || "",
      };
      // Si hay una marca seleccionada, filtrar por marca
      if (selectedMarca && selectedMarca._id) {
        params.marca = selectedMarca._id;
      }
      console.log("[CARGAR] Cargando modelos con params:", params);
      const response = await getModelos(params);
      console.log("[CARGAR] Response completa:", response);
      
      // Manejar diferentes estructuras de respuesta
      let modelosData = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          modelosData = response.data;
        } else if (Array.isArray(response.data.data)) {
          modelosData = response.data.data;
        } else if (response.data.modelos && Array.isArray(response.data.modelos)) {
          modelosData = response.data.modelos;
        }
      } else if (Array.isArray(response)) {
        modelosData = response;
      }
      
      console.log("[CARGAR] Modelos extraídos:", modelosData.length);
      
      // Ordenar modelos por coincidencias con las especificaciones del reloj
      const modelosOrdenados = ordenarModelosPorCoincidencias(
        modelosData,
        reloj.specifications,
        reloj.sku
      );
      
      const modelosFinales = Array.isArray(modelosOrdenados) ? modelosOrdenados : [];
      setModelos(modelosFinales);
      modelosRef.current = modelosFinales;
      setPage(1);
      
      const responseData = response.data || response;
      if (responseData.totalPages !== undefined) {
        setHasMore(responseData.totalPages > responseData.page);
      } else {
        setHasMore(true);
      }
    } catch (error) {
      console.error("[CARGAR] Error cargando modelos", error);
      setModelos([]);
      modelosRef.current = [];
    }
  };
  const cargarMasModelos = async () => {
    if (!hasMore) return;

    try {
      const newModelos = await getModelos({
        page: page + 1,
        limit,
        searchTerm,
      });
      setModelos((prev) => {
        const nuevos = [...prev, ...newModelos.data.data];
        modelosRef.current = nuevos;
        return nuevos;
      });
      setPage((prev) => prev + 1);
      setHasMore(newModelos.data.totalPages > newModelos.data.page);
    } catch (error) {
      console.error("Error cargando modelos", error);
    }
  };

  // Función para ordenar modelos por coincidencias con las especificaciones
  const ordenarModelosPorCoincidencias = (modelos, specifications, skuValue) => {
    if (!modelos || modelos.length === 0) return modelos;
    
    const materialCaja = specifications?.material_de_la_caja?.trim().toLowerCase() || "";
    const esfera = specifications?.esfera?.trim().toLowerCase() || "";
    const diametro = specifications?.di_metro?.trim().toLowerCase() || "";
    const sku = (skuValue || "").trim().toLowerCase();
    
    // Si no hay especificaciones, devolver sin ordenar
    if (!materialCaja && !esfera && !diametro && !sku) {
      return modelos;
    }
    
    return modelos.sort((a, b) => {
      let scoreA = 0;
      let scoreB = 0;
      
      // Comparar SKU (mayor peso)
      if (sku) {
        const skuA = (a.sku || "").toString().trim().toLowerCase();
        const skuB = (b.sku || "").toString().trim().toLowerCase();
        if (skuA === sku) scoreA += 10;
        if (skuB === sku) scoreB += 10;
        if (skuA.includes(sku)) scoreA += 5;
        if (skuB.includes(sku)) scoreB += 5;
      }
      
      // Comparar material de caja
      if (materialCaja) {
        const matA = (a.materialCaja || "").trim().toLowerCase();
        const matB = (b.materialCaja || "").trim().toLowerCase();
        if (matA === materialCaja) scoreA += 5;
        if (matB === materialCaja) scoreB += 5;
        if (matA.includes(materialCaja) || materialCaja.includes(matA)) scoreA += 2;
        if (matB.includes(materialCaja) || materialCaja.includes(matB)) scoreB += 2;
      }
      
      // Comparar esfera
      if (esfera) {
        const esfA = (a.colorEsfera || "").trim().toLowerCase();
        const esfB = (b.colorEsfera || "").trim().toLowerCase();
        if (esfA === esfera) scoreA += 5;
        if (esfB === esfera) scoreB += 5;
        if (esfA.includes(esfera) || esfera.includes(esfA)) scoreA += 2;
        if (esfB.includes(esfera) || esfera.includes(esfB)) scoreB += 2;
      }
      
      // Comparar diámetro
      if (diametro) {
        const diamA = (a.diametro || "").toString().trim().toLowerCase();
        const diamB = (b.diametro || "").toString().trim().toLowerCase();
        if (diamA === diametro) scoreA += 5;
        if (diamB === diametro) scoreB += 5;
        if (diamA.includes(diametro) || diametro.includes(diamA)) scoreA += 2;
        if (diamB.includes(diametro) || diametro.includes(diamB)) scoreB += 2;
      }
      
      // Ordenar por score descendente (mayor score primero)
      return scoreB - scoreA;
    });
  };

  const handleScroll = (event) => {
    const bottom =
      event.target?.scrollHeight - event.target?.scrollTop ===
      event.target?.clientHeight;
    if (bottom) {
      cargarMasModelos();
    }
  };

  return (
    <>
      <Card>
        <CardHeader title="Edit Reloj" />
        {loading ? (
          <div className="d-flex justify-content-center align-items-center">
            <CircularProgress className={classes.fancyLoader} />
          </div>
        ) : (
          <CardBody>
            {/* 1. Marca, código interno (SKU) y número de referencia (ref. fabricante) */}
            <SectionTitle first>Marca, código interno (SKU) y número de referencia</SectionTitle>
            <div className="row d-flex align-items-center">
              <div className="col-md-4">
                <Autocomplete
                  className="w-100 mr-5"
                  id="autocomplete-marca"
                  filterSelectedOptions
                  options={marcas}
                  getOptionLabel={(option) => option?.nombre}
                  value={selectedMarca}
                  onChange={(event, selected) => {
                    setSelectedMarca(selected);
                    setSelectedModelo(null);
                    setSearchTerm("");
                    setModeloTouched(false);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="normal"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      label="Marca"
                      required
                    />
                  )}
                />
              </div>
              <div className="col-md-4">
                <TextField
                  label="Código interno (SKU)"
                  value={reloj.sku}
                  onChange={(e) => handleChange("sku")(e)}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  required
                  helperText="Código interno vuestro para inventario"
                />
              </div>
              <div className="col-md-4">
                <TextField
                  label="Número de referencia"
                  value={reloj.specifications?.n_mero_de_referencia || ""}
                  onChange={handleSpecChange("n_mero_de_referencia")}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  placeholder="ej. WSSA0022, 16613"
                  helperText="Ref. del fabricante (la que ve el cliente)"
                  autoComplete="off"
                  inputProps={{ autoComplete: "off" }}
                />
              </div>
            </div>

            {/* 2. Material de la caja, Esfera, Diámetro */}
            <SectionTitle>Material de la caja, Esfera, Diámetro</SectionTitle>
            <div className="row d-flex align-items-center">
              <div className="col-md-4">
                <Autocomplete
                  freeSolo
                  options={suggestionsMaterialCaja}
                  value={reloj.specifications?.material_de_la_caja || ""}
                  onChange={(event, newValue) => handleSpecChange("material_de_la_caja")(newValue || "")}
                  onInputChange={(event, newInputValue) => handleSpecChange("material_de_la_caja")(newInputValue)}
                  openOnFocus={suggestionsMaterialCaja.length > 0}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Material de la caja"
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      helperText={suggestionsMaterialCaja.length > 0 ? `${suggestionsMaterialCaja.length} sugerencia(s) disponible(s)` : ""}
                    />
                  )}
                />
              </div>
              <div className="col-md-4">
                <Autocomplete
                  freeSolo
                  options={suggestionsEsfera}
                  value={reloj.specifications?.esfera || ""}
                  onChange={(event, newValue) => handleSpecChange("esfera")(newValue || "")}
                  onInputChange={(event, newInputValue) => handleSpecChange("esfera")(newInputValue)}
                  openOnFocus={suggestionsEsfera.length > 0}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Esfera"
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      helperText={suggestionsEsfera.length > 0 ? `${suggestionsEsfera.length} sugerencia(s) disponible(s)` : ""}
                    />
                  )}
                />
              </div>
              <div className="col-md-4">
                <Autocomplete
                  freeSolo
                  options={suggestionsDiametro}
                  value={reloj.specifications?.di_metro || ""}
                  onChange={(event, newValue) => handleSpecChange("di_metro")(newValue || "")}
                  onInputChange={(event, newInputValue) => handleSpecChange("di_metro")(newInputValue)}
                  openOnFocus={suggestionsDiametro.length > 0}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Diámetro"
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      helperText={suggestionsDiametro.length > 0 ? `${suggestionsDiametro.length} sugerencia(s) disponible(s)` : ""}
                    />
                  )}
                />
              </div>
            </div>
            <div className="row d-flex align-items-center">
              <div className="col-md-4">
                <TextField
                  label="Año de fabricación"
                  value={yearFromSpecs}
                  onChange={handleSpecChange("a_o_de_fabricaci_n")}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  placeholder="ej. 2025"
                  helperText="Opcional"
                />
              </div>
            </div>

            {/* 3. Modelo */}
            <SectionTitle>Modelo</SectionTitle>
            <div className="row d-flex align-items-center">
              <div className="col-12">
                <Autocomplete
                  className="w-100"
                  id="autocomplete-modelos"
                  options={modelosSelectOptions}
                  getOptionLabel={(option) => option?.nombre || ""}
                  inputValue={searchTerm}
                  onInputChange={(event, value, reason) => {
                    setSearchTerm(value);
                    if (reason === "input") setModeloTouched(true);
                    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
                    if (value.length === 0) {
                      if (selectedMarca && currentRef) cargarModelos(currentRef);
                      else if (selectedMarca) cargarModelos("");
                      return;
                    }
                    if (value.length >= 1) {
                      const delay = value.length <= 3 ? 100 : 200;
                      searchTimeoutRef.current = setTimeout(() => {
                        cargarModelos(value);
                        setPage(1);
                      }, delay);
                    }
                  }}
                  onChange={(event, selected) => {
                    setModeloTouched(true);
                    if (selected?.__create) {
                      setSelectedModelo(null);
                      setSearchTerm("");
                      setOpenAltaModelo(true);
                      return;
                    }
                    setSelectedModelo(selected);
                    setSearchTerm(selected?.nombre || "");
                    
                    // Al hacer clic en un modelo, autorellenar material de la caja, esfera y diámetro
                    if (selected && !selected.__create) {
                      const updates = {};
                      if (selected.materialCaja) {
                        updates.material_de_la_caja = selected.materialCaja;
                      }
                      if (selected.colorEsfera) {
                        updates.esfera = selected.colorEsfera;
                      }
                      if (selected.diametro) {
                        updates.di_metro = selected.diametro;
                      }
                      if (Object.keys(updates).length > 0) {
                        setReloj((prev) => ({
                          ...prev,
                          specifications: { ...prev.specifications, ...updates },
                        }));
                      }
                    }
                  }}
                  value={selectedModelo || null}
                  filterOptions={(options, state) => options}
                  openOnFocus={true}
                  renderOption={(option) => {
                    if (option?.__create) {
                      return (
                        <div style={{ cursor: "pointer", borderBottom: "1px solid #eee", width: "99%", padding: "10px 8px", display: "flex", alignItems: "center", gap: 10 }}>
                          <AddOutlined style={{ color: "#F64E60" }} />
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            <div style={{ fontWeight: 700 }}>{option.nombre}</div>
                            <div style={{ fontSize: 12, color: "#6b7280" }}>No hay coincidencia exacta por ref. Crear un modelo nuevo.</div>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div style={{ cursor: "pointer", borderBottom: "1px solid #eee", width: "99%", whiteSpace: "normal" }}>
                        <TarjetaModelo modelo={option} />
                      </div>
                    );
                  }}
                  renderInput={(params) => {
                    const selectedOpt = selectedModelo && !selectedModelo.__create ? selectedModelo : null;
                    const imgSrc = selectedOpt?.urlImage || selectedOpt?.urlImage1 || selectedOpt?.imageUrl || selectedOpt?.url;
                    const material = selectedOpt?.materialCaja || reloj.specifications?.material_de_la_caja;
                    const esfera = selectedOpt?.colorEsfera || reloj.specifications?.esfera;
                    const diam = selectedOpt?.diametro || reloj.specifications?.di_metro;
                    const chipStyle = { height: 22, fontSize: 12 };
                    return (
                      <TextField
                        {...params}
                        placeholder={selectedMarca && currentRef ? `Modelos sugeridos para ${selectedMarca.nombre} - Ref.: ${currentRef}` : "Buscar modelo por marca y número de referencia (o dejar vacío para crear uno nuevo)"}
                        variant="outlined"
                        margin="normal"
                        style={{ width: "100%" }}
                        helperText={
                          selectedOpt ? (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                              {material ? <Chip size="small" style={chipStyle} label={`Caja: ${material}`} /> : null}
                              {esfera ? <Chip size="small" style={chipStyle} label={`Esfera: ${esfera}`} /> : null}
                              {diam ? <Chip size="small" style={chipStyle} label={`Diámetro: ${diam}`} /> : null}
                            </div>
                          ) : selectedMarca && currentRef && !selectedModelo
                            ? (hasExactModeloMatchForRef ? "Puedes seleccionar un modelo. Están ordenados por coincidencias con las especificaciones." : "No hay coincidencia exacta por ref.: la primera sugerencia es crear un modelo.")
                            : ""
                        }
                        label="Modelo"
                        required
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              {selectedOpt && imgSrc ? (
                                <img src={imgSrc} alt="modelo" style={{ width: 34, height: 34, borderRadius: 6, objectFit: "cover", marginRight: 8, border: "1px solid #e5e7eb" }} onError={(e) => { e.currentTarget.style.display = "none"; }} />
                              ) : null}
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    );
                  }}
                  ListboxProps={{ onScroll: handleScroll }}
                  disabled={!selectedMarca}
                />
              </div>
            </div>

            {/* 4. Calificación/estado, Descripción del producto */}
            <SectionTitle>Calificación/estado, Descripción del producto</SectionTitle>
            <div className="row d-flex align-items-center">
              <div className="col-md-6">
                <TextField
                  label="Calificación / estado"
                  value={reloj.specifications?.estado?.calificacion || ""}
                  onChange={handleEstadoChange("calificacion")}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                />
              </div>
              <div className="col-md-6">
                <TextField
                  label="Descripción del producto"
                  value={reloj.specifications?.estado?.descripcion || ""}
                  onChange={handleEstadoChange("descripcion")}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                />
              </div>
            </div>

            {/* 5. Usado, Estuche, Documento */}
            <SectionTitle>Usado, Estuche, Documento</SectionTitle>
            <div className="row d-flex align-items-center">
              <div className="col-md-4">
                <FormControlLabel
                  control={<Checkbox checked={reloj.specifications?.estado?.usado || false} onChange={handleEstadoChange("usado")} />}
                  label="Usado"
                />
              </div>
              <div className="col-md-4">
                <FormControlLabel
                  control={<Checkbox checked={reloj.specifications?.contenido_de_la_entrega?.estuche || false} onChange={handleContenidoChange("estuche")} />}
                  label="Estuche"
                />
              </div>
              <div className="col-md-4">
                <FormControlLabel
                  control={<Checkbox checked={reloj.specifications?.contenido_de_la_entrega?.documento || false} onChange={handleContenidoChange("documento")} />}
                  label="Documento"
                />
              </div>
            </div>

            {/* 6. Nombre, descripción */}
            <SectionTitle>Nombre, descripción</SectionTitle>
            <div className="row d-flex align-items-center">
              <div className="col-12">
                <TextField
                  label="Nombre"
                  value={reloj.name}
                  onChange={handleChange("name")}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  required
                />
              </div>
            </div>
            <div className="row d-flex align-items-center" style={{ marginTop: 8 }}>
              <div className="col-12">
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                  Description
                </Typography>
                <Editor
                  body={reloj.description || ""}
                  setBody={(newBody) => setReloj({ ...reloj, description: newBody })}
                  placeholder="Descripción del reloj (puedes usar negrita, listas, etc.)"
                  className="max-height"
                />
              </div>
            </div>

            {/* 7. Precio, Vendedor */}
            <SectionTitle>Precio, Vendedor</SectionTitle>
            <div className="row d-flex align-items-center">
              <div className="col-md-4">
                <TextField
                  label="Price"
                  value={reloj.price?.amount ?? ""}
                  onChange={handlePriceChange("amount")}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  placeholder="0"
                  helperText="Opcional. Si se deja vacío se guardará como 0."
                />
              </div>
              <div className="col-md-4">
                <TextField
                  label="Vendedor - Nombre"
                  value={reloj.seller?.name || ""}
                  onChange={(e) => setReloj({ ...reloj, seller: { ...reloj.seller, name: e.target.value } })}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                />
              </div>
              <div className="col-md-4">
                <TextField
                  label="Vendedor - País"
                  value={reloj.seller?.country || ""}
                  onChange={(e) => setReloj({ ...reloj, seller: { ...reloj.seller, country: e.target.value } })}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                />
              </div>
            </div>

            {/* 8. Imágenes */}
            <SectionTitle>Imágenes</SectionTitle>
            <div className="row d-flex align-items-center">
              <div className="col-12 d-flex align-items-center mb-2">
                <Button variant="outlined" onClick={openImagePicker}>
                  Añadir imágenes
                </Button>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleImageFileChange}
                />
                <span style={{ marginLeft: 12, fontSize: 12, color: "#6b7280" }}>
                  Puedes cargar archivos o pegar URLs (abajo) / arrastrar para reordenar / usar flechas para mover / eliminar con la X.
                </span>
              </div>
              <div className="col-12 d-flex flex-wrap">
                {[...(reloj.images || []), ...newPreviews].map((src, idx) => (
                  <div
                    key={idx}
                    draggable
                    onDragStart={(e) => handleDragStart(e, idx)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, idx)}
                    style={{
                      position: "relative",
                      width: 120,
                      height: 120,
                      marginRight: 10,
                      marginBottom: 10,
                      borderRadius: 8,
                      overflow: "hidden",
                      border: draggedIndex === idx ? "2px solid #F64E60" : "1px solid #e5e7eb",
                      background: "#111827",
                      cursor: "move",
                      opacity: draggedIndex === idx ? 0.5 : 1,
                    }}
                  >
                    <img
                      src={buildImageUrl(src, SERVER_URL) || src}
                      alt={`img-${idx}`}
                      style={{ width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }}
                      onError={(e) => {
                        // Si falla la carga, intentar con la URL original
                        if (e.target.src !== src) {
                          e.target.src = src;
                        }
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: 4,
                        left: 4,
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveImage(idx, "up");
                        }}
                        disabled={idx === 0}
                        style={{
                          background: idx === 0 ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.6)",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          width: 24,
                          height: 24,
                          cursor: idx === 0 ? "not-allowed" : "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: 0,
                        }}
                        title="Mover arriba"
                      >
                        <ArrowUpward style={{ fontSize: 16 }} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveImage(idx, "down");
                        }}
                        disabled={idx === [...(reloj.images || []), ...newPreviews].length - 1}
                        style={{
                          background: idx === [...(reloj.images || []), ...newPreviews].length - 1 ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.6)",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          width: 24,
                          height: 24,
                          cursor: idx === [...(reloj.images || []), ...newPreviews].length - 1 ? "not-allowed" : "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: 0,
                        }}
                        title="Mover abajo"
                      >
                        <ArrowDownward style={{ fontSize: 16 }} />
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeImage(idx)}
                      style={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        background: "rgba(0,0,0,0.6)",
                        color: "#fff",
                        border: "none",
                        borderRadius: "50%",
                        width: 24,
                        height: 24,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                      }}
                      title="Eliminar"
                    >
                      ×
                    </button>
                    <div
                      style={{
                        position: "absolute",
                        bottom: 4,
                        left: 4,
                        background: "rgba(0,0,0,0.7)",
                        color: "#fff",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        fontSize: "11px",
                        fontWeight: "bold",
                      }}
                    >
                      #{idx + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="row d-flex align-items-center">
              <div className="col-12">
                <TextField
                  label="Imágenes (una URL por línea)"
                  value={(reloj.images || []).filter((img) => !isBase64Image(img)).join("\n")}
                  onChange={handleImagesChange}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  multiline
                  minRows={4}
                  placeholder="https://.../img1.jpg\nhttps://.../img2.jpg"
                  helperText="Las imágenes que tengan una URL del estilo https://IMAGEN.jpg van aquí, los archivos locales añadelos con el botón de Añadir Imágenes."
                />
              </div>
            </div>

            {/* 9. Visitas*/}
            <SectionTitle>Estado</SectionTitle>
            <div className="row d-flex align-items-center">
              <div className="col-md-4">
                <TextField
                  label="Vistas"
                  type="number"
                  value={reloj.views ?? 0}
                  onChange={(e) =>
                    setReloj({
                      ...reloj,
                      views: Number(e.target.value) || 0,
                    })
                  }
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  inputProps={{ min: 0 }}
                />
              </div>
            </div>
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: "16px 20px",
                background: "#f9fafb",
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
                marginTop: 12,
                flexWrap: "wrap",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reloj.active}
                    onChange={() => setReloj({ ...reloj, active: !reloj.active })}
                    color="primary"
                  />
                }
                label="Publicado"
              />
              {!reloj.active && (
                <Chip
                  label="No publicado"
                  style={{ backgroundColor: "#fef3c7", color: "#92400e" }}
                  size="small"
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reloj.sold || false}
                    onChange={() => setReloj({ ...reloj, sold: !reloj.sold })}
                    style={{ color: reloj.sold ? "#16a34a" : undefined }}
                  />
                }
                label="Vendido"
              />
              {reloj.sold && (
                <Chip
                  label="VENDIDO"
                  style={{ backgroundColor: "#dcfce7", color: "#166534", fontWeight: 600 }}
                  size="small"
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reloj.stock !== false}
                    onChange={() => setReloj({ ...reloj, stock: !reloj.stock })}
                    style={{ color: reloj.stock !== false ? "#10b981" : undefined }}
                  />
                }
                label="En stock"
              />
              {reloj.stock === false && (
                <Chip
                  label="AGOTADO"
                  style={{ backgroundColor: "#fee2e2", color: "#991b1b", fontWeight: 600 }}
                  size="small"
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reloj.reservado || false}
                    onChange={() => setReloj({ ...reloj, reservado: !reloj.reservado })}
                    style={{ color: reloj.reservado ? "#f59e0b" : undefined }}
                  />
                }
                label="Reservado"
              />
              {reloj.reservado && (
                <Chip
                  label="RESERVADO"
                  style={{ backgroundColor: "#fef3c7", color: "#92400e", fontWeight: 600 }}
                  size="small"
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reloj.destacado || false}
                    onChange={() => setReloj({ ...reloj, destacado: !reloj.destacado })}
                    style={{ color: reloj.destacado ? "#f59e0b" : undefined }}
                  />
                }
                label="Destacado"
              />
              {reloj.destacado && (
                <Chip
                  label="DESTACADO"
                  style={{ backgroundColor: "#fef3c7", color: "#92400e", fontWeight: 600 }}
                  size="small"
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reloj.novedad || false}
                    onChange={() => setReloj({ ...reloj, novedad: !reloj.novedad })}
                    style={{ color: reloj.novedad ? "#3b82f6" : undefined }}
                  />
                }
                label="Novedad"
              />
              {reloj.novedad && (
                <Chip
                  label="NOVEDAD"
                  style={{ backgroundColor: "#dbeafe", color: "#1e40af", fontWeight: 600 }}
                  size="small"
                />
              )}
              {reloj.pendingApproval && (
                <Chip
                  label="Pendiente de aprobación"
                  style={{ backgroundColor: "#e0f2fe", color: "#075985" }}
                  size="small"
                />
              )}
            </div>
          </CardBody>
        )}
      </Card>
      <Modal open={openAltaModelo} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <EditModeloPage close={handleClose} />
        </Box>
      </Modal>
      <Button
        onClick={() => history.push("/relojes")}
        variant="outlined"
        style={{ marginRight: "20px" }}
      >
        Back
      </Button>
      <Button
        onClick={handleSave}
        variant="outlined"
        color="primary"
        style={{ marginRight: "20px" }}
      >
        Save
      </Button>

      {relojId && (
        <>
          <MuiThemeProvider theme={theme}>
            <Button
              onClick={() => setOpenConfirmDialog(true)}
              variant="outlined"
              color="secondary"
            >
              Delete
            </Button>
          </MuiThemeProvider>

          <ConfirmDialog
            title={"Are you sure you want to delete this watch?"}
            open={openConfirmDialog}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              deleteReloj(relojId, reloj)
                .then((res) => {
                  if (res.status === 204) {
                    alertSuccess({
                      title: "Deleted!",
                      customMessage: "Delete successfully.",
                    });
                    history.push("/relojes");
                  }
                })
                .catch((error) => {
                  alertError({
                    error: error,
                    customMessage: "Could not save changes.",
                  });
                });
            }}
          />
        </>
      )}
    </>
  );
}
